/**
 * Payouts & reserves — a PER-PROCESSOR table (founder 2026-07-06: "one row per
 * processor" — Stripe/PayPal hold a reserve, PayArc doesn't). Each row: the
 * processor, its share of sales, how many days its money takes to land, and any
 * reserve it holds (% and for how long). We fold the rows into one
 * share-weighted cash delay the profit-model reads (Capital Return Velocity),
 * and show that effective number so the impact is visible. Legacy blended
 * accounts seed a single "All sales" row.
 */
import { Fragment, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useIsViewer } from "../lib/use-role.js";
import { useToast } from "../lib/toast.js";
import { Card, CardHead } from "./cards.js";
import { relockFinancialQueries } from "../lib/query-invalidation.js";

interface Payout { settlementDays: number; reservePct: number; reserveDays: number; settlementBaseDays?: number }
interface ProcReserve { name: string; sharePct: number; settlementDays?: number; reservePct: number; reserveDays: number }
interface Row { name: string; share: string; land: string; rPct: string; rDays: string }

const s = (n: number | undefined | null): string => (n === undefined || n === null || n === 0 ? "" : String(n));

export function PayoutReservesCard({ ready = true }: { ready?: boolean }): React.ReactElement {
  const api = useApiClient();
  const qc = useQueryClient();
  const toast = useToast();
  const isViewer = useIsViewer();

  const q = useQuery({
    queryKey: ["payout"],
    queryFn: () => api.get<{ payout: Payout | null; byProcessor: ProcReserve[] }>("/business-costs/payout"),
    enabled: ready,
  });

  const [rows, setRows] = useState<Row[] | null>(null);

  // Seed once from what's saved: per-processor rows if we have them, else one
  // blended "All sales" row from the single payout, else one empty row.
  useEffect(() => {
    if (rows !== null || !q.data) return;
    const p = q.data.payout;
    const base = p?.settlementBaseDays ?? p?.settlementDays ?? 0;
    if (q.data.byProcessor.length > 0) {
      setRows(q.data.byProcessor.map((x) => ({ name: x.name, share: s(x.sharePct), land: s(x.settlementDays ?? base), rPct: s(x.reservePct), rDays: s(x.reserveDays) })));
    } else if (p) {
      setRows([{ name: "All sales", share: "100", land: s(base), rPct: s(p.reservePct), rDays: s(p.reserveDays) }]);
    } else {
      setRows([{ name: "", share: "100", land: "", rPct: "", rDays: "" }]);
    }
  }, [q.data, rows]);

  const list = rows ?? [];
  const setRow = (i: number, patch: Partial<Row>): void => setRows((r) => (r ?? []).map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const addRow = (): void => setRows((r) => [...(r ?? []), { name: "", share: "", land: "", rPct: "", rDays: "" }]);
  const rmRow = (i: number): void => setRows((r) => { const n = (r ?? []).filter((_, j) => j !== i); return n.length ? n : [{ name: "", share: "100", land: "", rPct: "", rDays: "" }]; });

  const num = (v: string): number => (v.trim() === "" ? 0 : Number(v));

  // Live preview of the folded cash delay the profit-model will use — SAME
  // math the backend applies (share-weighted, equal weights when shares unknown).
  const effectiveDelay = useMemo(() => {
    if (list.length === 0) return null;
    const total = list.reduce((n, r) => n + num(r.share), 0);
    const w = (r: Row): number => (total > 0 ? num(r.share) / total : 1 / list.length);
    // Match the backend fold, which rounds land + reserve days to whole days
    // before weighting — so this preview equals the number CRV actually stores.
    return list.reduce((n, r) => n + w(r) * (Math.round(num(r.land)) + (num(r.rPct) / 100) * Math.round(num(r.rDays))), 0);
  }, [list]);

  // Share of sales is computed from the payment data, not entered here — so it
  // isn't a required field. Land days + reserve terms are what the user sets.
  const valid = list.every((r) =>
    r.name.trim() !== "" &&
    Number.isFinite(num(r.land)) && num(r.land) >= 0 && num(r.land) <= 60 &&
    (r.rPct.trim() === "" || (num(r.rPct) >= 0 && num(r.rPct) <= 100)) &&
    (r.rDays.trim() === "" || (num(r.rDays) >= 0 && num(r.rDays) <= 365)),
  ) && list.length > 0;

  const save = useMutation({
    mutationFn: () => api.post("/business-costs/payout/processors", {
      processors: list.map((r) => ({ name: r.name.trim(), sharePct: num(r.share), settlementDays: num(r.land), reservePct: num(r.rPct), reserveDays: num(r.rDays) })),
    }),
    onSuccess: () => {
      relockFinancialQueries(qc);
      toast.success("Saved — payout timing and reserves updated. They feed your cash cycle.");
      void qc.invalidateQueries({ queryKey: ["payout"] });
    },
    onError: () => toast.error("Couldn't save — check the numbers and try again."),
  });

  return (
    <Card>
      <CardHead
        title="Payouts & reserves"
        explain={[
          "<b>What it is:</b> For each payment processor — how many days its money takes to land, and any reserve it holds (a slice of every sale kept back and released later). Its share of your sales is read from your payment data, not entered here.",
          "<b>How it's used:</b> Both delay when your cash comes back. We blend the rows by each processor's share into one cash delay per sale — it lengthens your Capital Return Velocity and cash cycle.",
          "<b>Example:</b> Stripe (70% of sales) lands in 2 days and holds 5% for 70 days → its share adds about 2 + 3.5 = 5.5 days; PayArc (30%) lands in 2 days with no reserve → 2 days. Blended ≈ 4.5 days.",
        ]}
      />

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr style={{ color: "var(--text-faint)", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.03em" }}>
              <th style={{ textAlign: "left", padding: "6px 8px 6px 0" }}>Processor</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>Days to land</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>Reserve held %</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>…for how many days</th>
              <th style={{ padding: "6px 0 6px 8px" }} />
            </tr>
          </thead>
          <tbody>
            {list.map((r, i) => (
              <Fragment key={i}>
                <tr style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "8px 8px 8px 0" }}>
                    <input value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} placeholder="e.g. Stripe" style={{ ...cellBox, width: 150 }} disabled={isViewer} />
                    <div style={{ fontSize: 10.5, color: "var(--text-faint)", marginTop: 3, marginLeft: 2 }}>{num(r.share) > 0 ? `${Math.round(num(r.share))}% of sales` : "share from your data"}</div>
                  </td>
                  <td style={{ padding: "8px 8px", verticalAlign: "top" }}><input value={r.land} onChange={(e) => setRow(i, { land: e.target.value })} placeholder="2" inputMode="numeric" style={{ ...cellBox, width: 56, textAlign: "right" }} disabled={isViewer} /></td>
                  <td style={{ padding: "8px 8px", verticalAlign: "top" }}><input value={r.rPct} onChange={(e) => setRow(i, { rPct: e.target.value })} placeholder="0" inputMode="numeric" style={{ ...cellBox, width: 56, textAlign: "right" }} disabled={isViewer} /></td>
                  <td style={{ padding: "8px 8px", verticalAlign: "top" }}><input value={r.rDays} onChange={(e) => setRow(i, { rDays: e.target.value })} placeholder="0" inputMode="numeric" style={{ ...cellBox, width: 56, textAlign: "right" }} disabled={isViewer} /></td>
                  <td style={{ padding: "8px 0 8px 8px", textAlign: "right", verticalAlign: "top" }}>{!isViewer && list.length > 1 && <button type="button" onClick={() => rmRow(i)} title="Remove" style={{ ...ghost, padding: "4px 9px" }}>✕</button>}</td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginTop: 12 }}>
        <div style={{ fontSize: 12, color: "var(--body)" }}>
          {!isViewer && <button type="button" style={ghost} onClick={addRow}>+ Add a processor</button>}
        </div>
        {effectiveDelay !== null && (
          <div style={{ fontSize: 12.5, color: "var(--body)" }}>
            Blended cash delay: <b>{effectiveDelay.toFixed(1)} days</b> per sale
          </div>
        )}
      </div>

      {!isViewer && (
        <div style={{ marginTop: 12 }}>
          <button type="button" style={{ ...primaryBtn, opacity: valid && !save.isPending ? 1 : 0.5 }} disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      )}
      {isViewer && <div style={{ fontSize: 11.5, color: "var(--text-faint)", marginTop: 8 }}>Read-only access — an owner or admin sets these.</div>}
    </Card>
  );
}

const cellBox: React.CSSProperties = { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: "6px 9px", fontSize: 12.5, color: "var(--text)", fontFamily: "inherit" };
const primaryBtn: React.CSSProperties = { background: "var(--accent)", color: "var(--accent-contrast)", border: "none", borderRadius: "var(--r-sm)", padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" };
const ghost: React.CSSProperties = { background: "transparent", color: "var(--body)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: "6px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" };
